import { Dropdown } from "react-bootstrap";
import {
  BarChart,
  BoxArrowUpRight,
  Files,
  FileText,
  Folder,
  Pencil,
  People,
  QrCodeScan,
  Share,
  Sliders2,
  Trash,
} from "react-bootstrap-icons";
import React, { useEffect, useRef, useState } from "react";
import { useUserQuery } from "../../../../graphql/menu.hooks";
import {
  DeletePage2MutationFn,
  PagesByDomainDocument,
  PagesByDomainQuery,
  PagesByDomainQueryVariables,
  PagesByFolderDocument,
  PagesByFolderQuery,
  PagesByFolderQueryVariables,
} from "../../../../graphql/projects.hooks";
import { useTranslation } from "react-i18next";
import useGetUrlLocale from "../../../../hooks/useGetUrlLocale";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FolderDeleteMutationFn,
  FolderDuplicateMutationFn,
  useFolderDeleteMutation,
  useFolderDuplicateMutation,
  useFolderShowLazyQuery,
} from "../../../../graphql/folder.hooks";
import {
  useDuplicatePageMutation,
  usePageLazyQuery,
  usePagesByIdsByAdminLazyQuery,
  usePublishPageFromAdminPanelMutation,
  useUpdatePageScreeShotMutation,
} from "../../../../graphql/page.hooks";
import DeletePageFolderModal from "../../../../components/modals/deletePageFolderModal";
import CopyPageFolderModal from "../../../copyPageFolderModal";
import NoProjectModal from "../../../copyPageFolderModal/noProjectModal";
import { useDomainByIdLazyQuery } from "../../../../graphql/settings.hooks";
import { useIsNoShared } from "../../../../hooks/useIsNoShared";
import ChangePageTemplateModal, {
  PAGE_TYPE,
} from "../../../../components/modals/changePageTemplateModal";
import { FREE_LIMIT, ITEMS } from "../../../../utils";
import {
  duplicatePageModal,
  isLoadingOfDeletion,
  isPublishShow,
  isUpgradeShow,
  moveItem,
  publishLimitModal,
  renameFolder,
} from "../../../../graphql/state";
import { CustomToast } from "../../../../components/inputs/toast";
import { ApolloQueryResult, useReactiveVar } from "@apollo/client";
import { createPortal } from "react-dom";
import { makePagesScreenShots } from "../../../../utils/page/makePagesScreenShots";
import {
  ConfirmModal,
  ConfirmModalVariants,
} from "../../../../components/modals/confirmModal";

type RefetchPagesByDomain = (
  variables?: Partial<PagesByDomainQueryVariables>
) => Promise<ApolloQueryResult<PagesByDomainQuery>>;

type RefetchPagesByFolder = (
  variables?: Partial<PagesByFolderQueryVariables>
) => Promise<ApolloQueryResult<PagesByFolderQuery>>;

export type RefetchPages = RefetchPagesByDomain | RefetchPagesByFolder;
interface PageActionsProps {
  type: "folder" | "page" | "domain";
  deletePage?: DeletePage2MutationFn;
  deleteFolder?: FolderDeleteMutationFn;
  duplicateFolder?: FolderDuplicateMutationFn;
  deleteDomain?: (project: any) => void;
  refetchPagesByDomain?: RefetchPages;
  refetchAllPagesWithCredentials?: () => void;
  itemId?: number | string;
  accessType?: number | string;
  domainId?: number | string;
  pageId?: number | string;
  folderId?: number | string;
  folderPageAccessType?: number;
}

export default function PageActions({
  type,
  deletePage,
  deleteDomain,
  itemId,
  accessType,
  pageId,
  domainId,
  folderPageAccessType,
  refetchPagesByDomain,
  refetchAllPagesWithCredentials,
}: PageActionsProps) {
  const { projectId, id } = useParams();
  const lng = useGetUrlLocale();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [makeScreen] = useUpdatePageScreeShotMutation();
  const [getPages] = usePagesByIdsByAdminLazyQuery();

  const moveItemState = useReactiveVar(moveItem);
  const showDuplicatePageModal = useReactiveVar(duplicatePageModal);
  const { data: user, refetch: userRefetch } = useUserQuery();

  const isPremium = user?.user.activePremium;
  const domains = user?.user?.domains;

  const [deleteFolder] = useFolderDeleteMutation({
    onCompleted: () => {
      setTimeout(async () => {
        await refetchPagesByDomain();
        isLoadingOfDeletion(false);
      }, 1000);
    },
  });

  const [duplicateFolder] = useFolderDuplicateMutation({
    onCompleted: () => {
      setTimeout(() => {
        refetchPagesByDomain();
      }, 1000);
    },
  });
  const [duplicatePage] = useDuplicatePageMutation({
    onCompleted: () => {
      setTimeout(() => {
        refetchPagesByDomain();
      }, 1000);
    },
  });
  const [publishPage] = usePublishPageFromAdminPanelMutation({
    refetchQueries: [
      id
        ? {
            query: PagesByFolderDocument,
            variables: { folderId: Number(id) },
          }
        : projectId
          ? {
              query: PagesByDomainDocument,
              variables: { domainId: Number(projectId) },
            }
          : null,
    ].filter(Boolean),
  });

  const handleDeletePage = async () => {
    try {
      await deletePage({
        variables: {
          id: +pageId,
        },
      });
    } catch (error) {
      console.error("Error deleting page:", error);
    } finally {
      setTimeout(() => {
        isLoadingOfDeletion(false);
      }, 1000);
    }
  };

  const handleDeleteFolder = async () => {
    try {
      const { data } = await deleteFolder({ variables: { folderId: +itemId } });
      if (data?.folderDelete) {
        navigate(`${lng}/projects/${domainId}${id ? `/folder/${id}` : ""}`);
      }
    } catch (error) {
      console.error("Error deleting folder:", error);
    }
  };

  const handleDuplicateFolder = async () => {
    try {
      const { data } = await duplicateFolder({
        variables: { folderId: +itemId },
      });
      if (data?.folderDuplicate) {
        navigate(`${lng}/projects/${domainId}${id ? `/folder/${id}` : ""}`);
      }
    } catch (error) {
      console.error("Error duplicating folder:", error);
    }
  };

  const handleDuplicatePage = async () => {
    if (user) {
      if (
        user?.user?.countMyPages >= FREE_LIMIT.PAGE &&
        !user?.user.admin &&
        !isPremium
      ) {
        isUpgradeShow(true);
        return;
      }
    }
    try {
      const { data } = await duplicatePage({
        variables: { id: +itemId, duplicateFile: true },
      });
      if (data?.duplicatePage) {
        navigate(`${lng}/projects/${projectId}${id ? `/folder/${id}` : ""}`);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handlePublishPage = async () => {
    if (!user?.user?.canPublish) {
      publishLimitModal(true);
      return;
    }
    window.gtag?.("event", "publish_page");
    try {
      const { data } = await publishPage({
        variables: {
          pageId: +itemId,
        },
        refetchQueries: ["PageNotificationsForUser"],
      });
      if (data?.publishPageFromAdminPanel && window.self === window.top) {
        isPublishShow({ isActive: true, pageId: +itemId, sharePage: false });
        refetchAllPagesWithCredentials();
        userRefetch();
      }
    } catch (error) {
      setShowFailedPublishToast(true);
      console.error(error);
    }
  };
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showNoProjectModal, setShowNoProjectModal] = useState(false);
  const [showPageTemplateModal, setShowPageTemplateModal] = useState(false);
  const [pageTemplateModalType, setShowPageTemplateModalType] =
    useState<PAGE_TYPE>();
  const [showFailedPublishToast, setShowFailedPublishToast] = useState(false);

  const handleShowModal = () => setShowDeleteModal(true);
  const handleCloseModal = () => setShowDeleteModal(false);
  const handleShowCopyModal = () => setShowCopyModal(true);
  const handleCloseCopyModal = () => setShowCopyModal(false);
  const handleShowPageTemplateModal = (type: PAGE_TYPE) => {
    setShowPageTemplateModalType(type);
    setShowPageTemplateModal(true);
  };
  const handleClosePageTemplateModal = () => setShowPageTemplateModal(false);

  const handleShowNoProjectModal = () => setShowNoProjectModal(true);
  const handleCloseNoProjectModal = () => setShowNoProjectModal(false);
  const [domainFetch, { data: domain }] = useDomainByIdLazyQuery();

  const [folderFetch, { data: folder }] = useFolderShowLazyQuery();

  const [fetchPage, { data: page }] = usePageLazyQuery();
  const isNoShared = useIsNoShared();

  const ref = useRef(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    if (type === "domain") {
      domainFetch({ variables: { domainId: +domainId || +projectId } });
    }
    if (id) {
      folderFetch({ variables: { folderId: +id } });
    }
  }, [
    id,
    domainFetch,
    folderFetch,
    fetchPage,
    pageId,
    projectId,
    domainId,
    type,
  ]);

  useEffect(() => {
    if (type === "page") {
      fetchPage({ variables: { id: +pageId } });
    }
  }, [type, pageId, fetchPage]);

  let accessTypeOwner: string | undefined;

  if (type === "domain") {
    accessTypeOwner = domain?.domain?.user?.email;
  } else if (type === "page") {
    accessTypeOwner = page?.page?.user?.email;
  } else if (type === "folder") {
    accessTypeOwner = folder?.folderShow.folder.user?.email;
  } else {
    accessTypeOwner = undefined;
  }
  const isOwner = accessTypeOwner === user?.user?.email;

  const openMovePageModal = () => {
    moveItem({
      ...moveItemState,
      isActive: true,
      data: { folderIds: [], pageIds: [itemId] },
    });
  };

  const openMoveFolderModal = () => {
    moveItem({
      ...moveItemState,
      isActive: true,
      data: { pageIds: [], folderIds: [itemId] },
    });
  };

  const openSharePageModal = () => {
    isPublishShow({ isActive: true, pageId: +itemId, sharePage: true });
  };

  const tryMakeScreenShot = async () => {
    const { data } = await getPages({
      variables: { ids: [+itemId] },
    });

    await makePagesScreenShots(data?.pagesByIdsByAdmin, makeScreen);
  };

  const menu =
    visible && position ? (
      <>
        <Dropdown.Menu>
          {type === "page" && (
            <>
              {folderPageAccessType === 0 ||
              (!accessType && !folderPageAccessType) ||
              accessType === 2 ||
              (folderPageAccessType && folderPageAccessType !== 1) ? (
                <>
                  <Dropdown.Item
                    as={Link}
                    target={"_blank"}
                    to={`${process.env.REACT_APP_CONSTRUCTOR_URL}${lng}/${pageId}`}
                    className="py-2 px-3 mb-1"
                  >
                    <Pencil className="me-2" />
                    {t("projects.editPage")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    as={Link}
                    to={`${lng}/statistic-dashboard/${pageId}`}
                    className="py-2 px-3 mb-1"
                  >
                    <BarChart className="me-2" />
                    {t("menu.statistics")}
                  </Dropdown.Item>
                </>
              ) : null}
              <Dropdown.Item
                as={Link}
                to={`${lng}/entry/${pageId}`}
                className={`py-2 px-3 mb-1 ${page?.page?.page.publishedAt === null && "disabled-block"}`}
              >
                <QrCodeScan className="me-2" />
                {t("qr.getQR")}
              </Dropdown.Item>
              <Dropdown.Item
                as={Link}
                to={`${process.env.REACT_APP_CONSTRUCTOR_URL}${lng}/${pageId}?preview=true`}
                className="py-2 px-3 mb-1"
                target={"_blank"}
              >
                <FileText className="me-2" />
                {t("projects.preview")}
              </Dropdown.Item>

              <Dropdown.Item
                className="py-2 px-3 mb-1"
                as={Link}
                to={{
                  pathname: `${lng}/user/access/page/${pageId}/access`,
                  search: `?${id ? `parentFolder=${id}` : ""}${!isNoShared ? `&Shared` : ""}`,
                }}
              >
                <People className="me-2" />
                {t("share.title")}
              </Dropdown.Item>
              <Dropdown.Item
                className="py-2 px-3 mb-1"
                onClick={openSharePageModal}
              >
                <Share className="me-2" />
                {t("share.sharePage")}
              </Dropdown.Item>
              {folderPageAccessType === 0 ||
              (!accessType && !folderPageAccessType) ||
              accessType === 2 ||
              (folderPageAccessType && folderPageAccessType !== 1) ? (
                <>
                  <Dropdown.Item
                    as={Link}
                    to={{
                      pathname: `${lng}/page/${pageId}/edit`,
                      search: `?${id ? `parentFolder=${id}` : ""}${!isNoShared ? `&Shared` : ""}`,
                    }}
                    className="py-2 px-3 mb-1"
                  >
                    <Sliders2 className="me-2" />
                    {t("projects.pageSettings")}
                  </Dropdown.Item>
                </>
              ) : null}
              {(!accessType &&
                folderPageAccessType === 0 &&
                (isOwner || !accessTypeOwner)) ||
              (!accessType &&
                !folderPageAccessType &&
                (isOwner || !accessTypeOwner)) ||
              (!accessType &&
                folderPageAccessType === 2 &&
                (isOwner || !accessTypeOwner)) ? (
                <Dropdown.Item
                  className="py-2 px-3 mb-1"
                  onClick={openMovePageModal}
                >
                  <Folder className="me-2" />
                  {t("projects.movePage")}
                </Dropdown.Item>
              ) : null}

              {(accessType === 2 || isOwner) && (
                <Dropdown.Item
                  className="py-2 px-3 mb-1"
                  onClick={() => {
                    if (!accessType && isNoShared) {
                      if (page?.page?.subPages.length > 0) {
                        duplicatePageModal(+itemId);
                        return;
                      }
                      handleDuplicatePage();
                    } else {
                      {
                        domains.length === 0
                          ? handleShowNoProjectModal()
                          : handleShowCopyModal();
                      }
                    }
                  }}
                >
                  <Files className="me-2" />
                  {t("projects.makeCopy")}
                </Dropdown.Item>
              )}
              {folderPageAccessType === 0 ||
              (!accessType && !folderPageAccessType) ||
              accessType === 2 ||
              (folderPageAccessType && folderPageAccessType !== 1) ? (
                <Dropdown.Item
                  className="py-2 px-3 mb-1"
                  onClick={handlePublishPage}
                >
                  <BoxArrowUpRight className="me-2" />
                  {t("projects.publishPage")}
                </Dropdown.Item>
              ) : null}
              {(!accessType &&
                folderPageAccessType === 0 &&
                (isOwner || !accessTypeOwner)) ||
              (!accessType &&
                !folderPageAccessType &&
                (isOwner || !accessTypeOwner)) ||
              (!accessType &&
                folderPageAccessType === 2 &&
                (isOwner || !accessTypeOwner)) ? (
                <>
                  <Dropdown.Item
                    className="py-2 px-3"
                    onClick={handleShowModal}
                  >
                    <Trash className="me-2" />
                    {t("projects.deletePage")}
                  </Dropdown.Item>
                  <DeletePageFolderModal
                    itemType={ITEMS.PAGE}
                    title={
                      page?.page?.subPages.length > 0
                        ? t("projects.deleteParentPage")
                        : t("projects.deletePage")
                    }
                    description={
                      page?.page?.subPages.length > 0
                        ? t("projects.deleteParentPageDescription")
                        : t("projects.deletePageDescription")
                    }
                    additionText={
                      page?.page?.subPages.length > 0
                        ? t("projects.undone")
                        : t("projects.permanently")
                    }
                    showDeleteModal={showDeleteModal}
                    handleCloseModal={handleCloseModal}
                    handleConfirmDelete={handleDeletePage}
                  />
                </>
              ) : null}
              {user?.user.admin && (
                <>
                  <Dropdown.Item
                    className="py-2 px-3 mb-1"
                    onClick={() => handleShowPageTemplateModal(PAGE_TYPE.MEQR)}
                  >
                    ME-QR Order
                  </Dropdown.Item>
                  <Dropdown.Item
                    className="py-2 px-3 mb-1"
                    onClick={() =>
                      handleShowPageTemplateModal(PAGE_TYPE.MEPAGE)
                    }
                  >
                    ME-Page Order
                  </Dropdown.Item>
                  <Dropdown.Item
                    className="py-2 px-3 mb-1"
                    onClick={tryMakeScreenShot}
                  >
                    Post ScreenShot
                  </Dropdown.Item>
                  {showPageTemplateModal && (
                    <ChangePageTemplateModal
                      handleCloseModal={handleClosePageTemplateModal}
                      pageType={pageTemplateModalType}
                      pageId={+itemId}
                    />
                  )}
                </>
              )}
            </>
          )}
          {type === "folder" && (
            <>
              {folderPageAccessType === 0 ||
              (!accessType && !folderPageAccessType) ||
              accessType === 2 ||
              (folderPageAccessType && folderPageAccessType !== 1) ? (
                <>
                  <Dropdown.Item
                    className="py-2 px-3 mb-1"
                    onClick={() =>
                      renameFolder({ isActive: true, folderId: +itemId })
                    }
                  >
                    <Pencil className="me-2" />
                    {t("projects.renameFolder")}
                  </Dropdown.Item>
                </>
              ) : null}

              <Dropdown.Item
                className="py-2 px-3 mb-1"
                as={Link}
                to={`${lng}/user/access/folder/${itemId}/access`}
              >
                <People className="me-2" />
                {t("share.title")}
              </Dropdown.Item>
              {(!accessType &&
                folderPageAccessType === 0 &&
                (isOwner || !accessTypeOwner)) ||
              (!accessType &&
                !folderPageAccessType &&
                (isOwner || !accessTypeOwner)) ||
              (!accessType &&
                folderPageAccessType === 2 &&
                (isOwner || !accessTypeOwner)) ? (
                <>
                  <Dropdown.Item
                    className="py-2 px-3 mb-1"
                    onClick={openMoveFolderModal}
                  >
                    <Folder className="me-2" />
                    {t("projects.moveFolder")}
                  </Dropdown.Item>
                </>
              ) : null}

              {accessType === 2 && (
                <Dropdown.Item
                  className="py-2 px-3 mb-1"
                  onClick={() => {
                    if (!accessType) {
                      handleDuplicateFolder();
                    } else {
                      {
                        domains.length === 0
                          ? handleShowNoProjectModal()
                          : handleShowCopyModal();
                      }
                    }
                  }}
                >
                  <Files className="me-2" />
                  {t("projects.makeCopy")}
                </Dropdown.Item>
              )}

              {(!accessType &&
                folderPageAccessType === 0 &&
                (isOwner || !accessTypeOwner)) ||
              (!accessType &&
                !folderPageAccessType &&
                (isOwner || !accessTypeOwner)) ||
              (!accessType &&
                folderPageAccessType === 2 &&
                (isOwner || !accessTypeOwner)) ? (
                <>
                  <Dropdown.Item
                    className="py-2 px-3 mb-1"
                    onClick={handleShowModal}
                  >
                    <Trash className="me-2" />
                    {t("projects.deleteFolder")}
                  </Dropdown.Item>
                  <DeletePageFolderModal
                    itemType={ITEMS.FOLDER}
                    title={t("projects.deleteFolder")}
                    description={t("projects.deleteFolderDescription")}
                    additionText={t("projects.permanentlyFolder")}
                    showDeleteModal={showDeleteModal}
                    handleCloseModal={handleCloseModal}
                    handleConfirmDelete={handleDeleteFolder}
                  />
                </>
              ) : null}
            </>
          )}
          {type === "domain" && (
            <>
              {(!accessType || accessType === 2) && (
                <Dropdown.Item
                  as={Link}
                  to={`${lng}/projects/${domainId}/edit`}
                  className="py-2 px-3 mb-1"
                >
                  <Sliders2 className="me-2" />
                  {t("projects.settings")}
                </Dropdown.Item>
              )}

              <Dropdown.Item
                className="py-2 px-3 mb-1"
                as={Link}
                to={`${lng}/user/access/domain/${domainId}/access`}
              >
                <People className="me-2" />
                {t("share.title")}
              </Dropdown.Item>
              {isOwner && (
                <Dropdown.Item
                  className="py-2 px-3 mb-1"
                  href="#"
                  onClick={() => deleteDomain(domain)}
                >
                  <Trash className="me-2" />
                  {t("projects.delete")}
                </Dropdown.Item>
              )}
            </>
          )}
        </Dropdown.Menu>
        <CopyPageFolderModal
          folderItemId={itemId}
          pageId={pageId}
          closeModal={handleCloseCopyModal}
          showCopyModal={showCopyModal}
          type={type}
        />
        <NoProjectModal
          closeModal={handleCloseNoProjectModal}
          showModal={showNoProjectModal}
        />
        <ConfirmModal
          variant={ConfirmModalVariants.SECONDARY}
          show={+itemId === showDuplicatePageModal}
          title={t("projects.copyParentPage")}
          description={t("projects.copyParentPageDescription")}
          confirmText={t("projects.duplicatePage")}
          onConfirm={handleDuplicatePage}
          onCancel={() => duplicatePageModal(null)}
        />
        <div
          className="position-fixed bottom-0 start-50 translate-middle-x"
          style={{ zIndex: 2 }}
        >
          <CustomToast
            isShow={showFailedPublishToast}
            setShow={setShowFailedPublishToast}
            message={`${t("projects.Your page is empty")}`}
            background="bg-danger"
          />
        </div>
      </>
    ) : null;

  return (
    <>
      <div ref={ref} />
      {createPortal(menu, document.body)}
    </>
  );
}
